/**
 * Revision Store — named snapshots of project state stored in IndexedDB.
 *
 * v2 (UX Pack F1a): records are PROJECT-SCOPED. Every record carries
 * `projectId` (backfilled from the snapshot's own state for v1 records;
 * unassignable legacy records are quarantined with projectId null — kept,
 * listed only under the explicit `unassigned` view, never silently deleted)
 * and an explicit `kind`: 'checkpoint' | 'manual' | 'auto' — never inferred
 * from name or note strings. Named checkpoints are NEVER auto-pruned: pruning
 * consumes manual/auto snapshots oldest-first and surfaces a storage-limit
 * error instead of evicting a checkpoint. All operations (list, get, rename,
 * delete, clear, prune, quota recovery, restore) are scoped to the active
 * project; restore revalidates the project id and load epoch immediately
 * before applying.
 */

import { store } from './state.js';
import { migrateState, applyLoadedProjectState } from './save-load.js';
import { createIdbHandle, withStore, promisifyRequest } from './idb.js';
import { reportError } from './report-error.js';

const DB_NAME = 'pb-revisions';
const DB_VERSION = 2;
const STORE_NAME = 'snapshots';
/**
 * Exported (TL-4) so the History Timeline's header can state the bound rather
 * than duplicating the numbers. `ui/history-timeline/timeline-model.js` mirrors
 * them as literals — it cannot import this module without dragging
 * `core/state.js` into the `core/history.js` cycle — and its own suite asserts
 * the two agree against this source.
 */
export const MAX_REVISIONS = 50;
export const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB soft cap
const MAX_NAME_LENGTH = 80;

/**
 * THE SAFETY-NET EXEMPTION (TL-4).
 *
 * `prune()`'s two passes and `_doSave`'s quota-recovery victim list all
 * selected on `kind !== 'checkpoint'` alone — and the "Before restore" record
 * is `kind: 'auto'`. On a store that is nearly all checkpoints, the `prune()`
 * that `restore()` itself calls next could therefore delete the net it had
 * just written, and nothing anywhere would say so.
 *
 * `protected: true` is that one bit. It is additive, needs no DB_VERSION bump
 * (an absent field reads falsy), and is honoured by every deletion policy in
 * this file. Exactly ONE record carries it at a time: the newest safety point
 * clears the flag from its predecessor.
 */
const isEvictable = (rev) => rev.kind !== 'checkpoint' && rev.protected !== true;

export const REVISION_KINDS = Object.freeze(['checkpoint', 'manual', 'auto']);

/** Trimmed, 1–80 chars → the normalized name; anything else → null. */
export function validateRevisionName(name) {
  const trimmed = String(name ?? '').trim();
  if (!trimmed || trimmed.length > MAX_NAME_LENGTH) return null;
  return trimmed;
}

// Shared self-healing connection handle. Gains abort rejection so a hung tx can
// no longer wedge _saveQueue. v2 adds the by-project index; the record-level
// backfill runs lazily (_ensureMigrated) so it works identically under the
// scriptable fake IndexedDB the behavioral tests drive.
const revisionIdb = createIdbHandle({
  name: DB_NAME,
  version: DB_VERSION,
  upgrade: (db, oldVersion, _newVersion, request) => {
    let objStore;
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      objStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      objStore.createIndex('by-timestamp', 'timestamp');
    } else {
      objStore = request?.transaction?.objectStore?.(STORE_NAME);
    }
    if (objStore && !(objStore.indexNames?.contains?.('by-project') ?? false)) {
      objStore.createIndex('by-project', 'projectId');
    }
  }
});

export class RevisionStore {
  /**
   * Injection seams default to the live singletons; behavioral tests drive the
   * REAL class against a fake IndexedDB handle and scripted project/epoch.
   */
  constructor({
    getProjectId = () => store.getState()?.projectId || null,
    getEpoch = () => store.getEpoch(),
    cloneCurrentState = () => structuredClone(store.getState()),
    applyState = (restored, opts) => applyLoadedProjectState(restored, opts),
    migrate = (state) => migrateState(state),
    clearHistory = () => store.history.clear(),
    idb = revisionIdb
  } = {}) {
    this._saveQueue = Promise.resolve();
    this._getProjectId = getProjectId;
    this._getEpoch = getEpoch;
    this._cloneCurrentState = cloneCurrentState;
    this._applyState = applyState;
    this._migrate = migrate;
    this._clearHistory = clearHistory;
    this._idb = idb;
    this._migrated = null;
  }

  /**
   * Save a revision. Queued to prevent concurrency issues during prune.
   * Manual saves return a promise that rejects on error.
   * Auto saves are fire-and-forget.
   * `kind` defaults from the trigger ('auto' → 'auto', else 'manual');
   * pass 'checkpoint' explicitly for named checkpoints.
   *
   * `state` (TL-6, additive — every existing caller omits it) snapshots a state
   * the CALLER already holds instead of the live one. Pinning a session row
   * needs exactly that: `computeHistoricState(store, ref)` reconstructs a past
   * state without touching the store, and cloning the live state instead would
   * store the wrong moment under the right name — a failure with no symptom
   * until the day you restore it.
   */
  save(name, note = '', trigger = 'manual', kind = null, state = undefined) {
    let rejectManual;
    const manualPromise =
      trigger !== 'auto'
        ? new Promise((resolve, reject) => {
            rejectManual = { resolve, reject };
          })
        : undefined;

    this._saveQueue = this._saveQueue
      .then(() => this._doSave(name, note, trigger, kind, state))
      .then(() => {
        rejectManual?.resolve();
      })
      .catch((err) => {
        if (trigger === 'auto') {
          console.warn('[RevisionStore] Auto-snapshot failed:', err.message);
        } else {
          rejectManual?.reject(err);
        }
      });

    return manualPromise ?? this._saveQueue;
  }

  /**
   * UTF-8 byte size of a value's JSON serialization. Multibyte characters (accented
   * names, emoji, CJK) are one UTF-16 code unit each via `.length` but 2–4 bytes on
   * disk, so `.length` undercounts and the size-based prune cap runs late; TextEncoder
   * gives the true stored byte count. Old records sized by `.length` are tolerated.
   */
  _byteSize(value) {
    const json = typeof value === 'string' ? value : JSON.stringify(value);
    return new TextEncoder().encode(json).length;
  }

  _resolveKind(trigger, kind) {
    if (kind && REVISION_KINDS.includes(kind)) return kind;
    return trigger === 'auto' ? 'auto' : 'manual';
  }

  async _doSave(name, note, trigger, kind, providedState) {
    await this._ensureMigrated();
    const resolvedKind = this._resolveKind(trigger, kind);
    const validName = validateRevisionName(name);
    if (!validName && resolvedKind === 'checkpoint') {
      throw new Error(`Checkpoint names must be 1–${MAX_NAME_LENGTH} characters`);
    }
    const state = providedState === undefined ? this._cloneCurrentState() : providedState;
    const stateSize = this._byteSize(state);
    const record = {
      id: crypto.randomUUID(),
      name: validName || String(name ?? '').slice(0, MAX_NAME_LENGTH) || 'Snapshot',
      note,
      trigger,
      kind: resolvedKind,
      projectId: this._getProjectId() ?? state?.projectId ?? null,
      timestamp: Date.now(),
      state,
      stateSize
    };

    try {
      await this._put(record);
    } catch (err) {
      if (err.name === 'QuotaExceededError') {
        // Quota recovery consumes THIS project's oldest non-checkpoint
        // snapshots. Named checkpoints are never sacrificed for space, and
        // neither is the protected safety point (TL-4) — a save that frees
        // room by eating the net a restore is standing on trades one failure
        // for a worse one.
        const all = await this.list();
        const victims = all.filter(isEvictable).slice(-10);
        for (const rev of victims) await this.delete(rev.id);
        try {
          await this._put(record);
        } catch (retryErr) {
          reportError('Revisions', retryErr, {
            user: 'toast',
            message: 'Could not save a project snapshot — storage is full',
            detail: 'revision store still over quota after pruning',
            id: 'revision-quota'
          });
          throw retryErr;
        }
      } else {
        throw err;
      }
    }
    await this.prune();
  }

  async _put(record) {
    await withStore(this._idb, STORE_NAME, 'readwrite', (objStore) => {
      objStore.put(record);
    });
  }

  /**
   * Write a PROTECTED safety point, and CONFIRM IT LANDED (TL-4).
   *
   * The read-back is the whole method. `restore()` used to promise a "Before
   * restore" snapshot unconditionally in a confirm dialog and then write it
   * best-effort AFTER the point of no return, logging failures to
   * `console.warn`. Two independent things could make that promise false and
   * neither was observable: the write rejecting, and `prune()` — which cannot
   * fail, it toasts and returns normally — deleting the record on the very
   * next line.
   *
   * So capacity is decided by reading the record back, never by prune's
   * silence. A net you have not seen on disk is not a net.
   *
   * @param {string} name
   * @param {object} state  captured by the caller, SYNCHRONOUSLY, before any await
   * @returns {Promise<object>} the confirmed record
   */
  async saveProtected(name, state) {
    await this._ensureMigrated();
    const record = {
      id: crypto.randomUUID(),
      name: validateRevisionName(name) || 'Before restore',
      note: '',
      trigger: 'auto',
      kind: 'auto',
      protected: true,
      projectId: this._getProjectId() ?? state?.projectId ?? null,
      timestamp: Date.now(),
      state,
      stateSize: this._byteSize(state)
    };

    await this._put(record);

    // Read back THROUGH `get()` — the same scoped path every reader uses — so
    // this proves the record is not merely written but findable. A record
    // written under a projectId the reader cannot see is indistinguishable
    // from no record at all.
    const confirmed = await this.get(record.id);
    if (!confirmed || confirmed.protected !== true) {
      throw new Error('Safety snapshot could not be confirmed on disk');
    }
    return confirmed;
  }

  /** Drop the protected flag from every OTHER safety point, newest kept. */
  async _releasePreviousProtected(keepId) {
    const all = await this.list();
    for (const rev of all) {
      if (rev.id === keepId || rev.protected !== true) continue;
      await this._put({ ...rev, protected: false });
    }
  }

  /**
   * Can a safety point be written at all? Read BEFORE the commit bar renders,
   * so P17's refusal is on the disabled control rather than an error after the
   * click.
   *
   * "Full" is not "at the cap" — it is at the cap with nothing `prune()` is
   * allowed to evict. A store holding 50 records of which 40 are ordinary
   * snapshots has room; one holding 50 named points does not.
   */
  async safetyPointCapacity() {
    const all = await this.list();
    const bytes = all.reduce((sum, r) => sum + (r.stateSize || 0), 0);
    const evictable = all.filter(isEvictable).length;
    const atCap = all.length >= MAX_REVISIONS || bytes >= MAX_TOTAL_SIZE;
    return {
      ok: !(atCap && evictable === 0),
      kept: all.length,
      cap: MAX_REVISIONS,
      bytes,
      byteCap: MAX_TOTAL_SIZE,
      evictable
    };
  }

  /**
   * One-time lazy backfill of v1 records: stamp `projectId` (from the
   * snapshot's own captured state; unassignable → null = quarantined) and
   * `kind` (from the trigger). Idempotent; a failure resets so the next call
   * retries instead of silently operating on unscoped records.
   */
  async _ensureMigrated() {
    if (this._migrated) return this._migrated;
    this._migrated = (async () => {
      const all = await withStore(this._idb, STORE_NAME, 'readonly', (objStore) =>
        promisifyRequest(objStore.index('by-timestamp').getAll())
      );
      for (const rec of all || []) {
        const hasProject = Object.prototype.hasOwnProperty.call(rec, 'projectId');
        if (hasProject && rec.kind) continue;
        const derived =
          typeof rec?.state?.projectId === 'string' && rec.state.projectId
            ? rec.state.projectId
            : null;
        await this._put({
          ...rec,
          projectId: hasProject ? rec.projectId : derived,
          kind: rec.kind || (rec.trigger === 'auto' ? 'auto' : 'manual')
        });
      }
    })().catch((err) => {
      this._migrated = null;
      throw err;
    });
    return this._migrated;
  }

  /**
   * List revisions sorted newest first — scoped to the ACTIVE project.
   * Options: `kind` filters to one kind; `unassigned: true` lists the
   * quarantined legacy records (projectId null) instead.
   */
  async list({ kind = null, unassigned = false } = {}) {
    await this._ensureMigrated();
    let results;
    if (unassigned) {
      const all = await withStore(this._idb, STORE_NAME, 'readonly', (objStore) =>
        promisifyRequest(objStore.index('by-timestamp').getAll())
      );
      results = (all || []).filter((rec) => rec.projectId == null);
    } else {
      const projectId = this._getProjectId();
      if (!projectId) return [];
      results = await withStore(this._idb, STORE_NAME, 'readonly', (objStore) =>
        promisifyRequest(objStore.index('by-project').getAll(projectId))
      );
      results = results || [];
    }
    if (kind) results = results.filter((rec) => rec.kind === kind);
    results.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    return results;
  }

  /**
   * Get a single revision by ID — only the active project's records (or
   * quarantined unassigned ones) are visible; other projects' records read
   * as absent.
   */
  async get(id) {
    await this._ensureMigrated();
    const record = await withStore(this._idb, STORE_NAME, 'readonly', (objStore) =>
      promisifyRequest(objStore.get(id))
    );
    if (!record) return null;
    if (record.projectId != null && record.projectId !== this._getProjectId()) return null;
    return record;
  }

  /** Delete a revision by ID (scoped — cross-project deletes are no-ops). */
  async delete(id) {
    const record = await this.get(id);
    if (!record) return;
    await withStore(this._idb, STORE_NAME, 'readwrite', (objStore) => {
      objStore.delete(id);
    });
  }

  /** Update a revision's name (validated; scoped like get/delete). */
  async rename(id, newName) {
    const validName = validateRevisionName(newName);
    if (!validName) throw new Error(`Names must be 1–${MAX_NAME_LENGTH} characters`);
    const rev = await this.get(id);
    if (!rev) return;
    rev.name = validName;
    await this._put(rev);
  }

  /**
   * Delete all of the ACTIVE project's revisions. Quarantined legacy records
   * and other projects' records are untouched. (Method name kept for the
   * existing panel wiring; the semantics are project-scoped as of v2.)
   */
  async clearAll() {
    const all = await this.list();
    for (const rev of all) await this.delete(rev.id);
  }

  /**
   * Prune the active project's snapshots by count, then by total size.
   * Manual/auto snapshots go oldest-first; named checkpoints are NEVER
   * auto-pruned — a checkpoint-only overflow surfaces a storage-limit error
   * (id 'revision-quota') and deletes nothing.
   */
  async prune() {
    let all = await this.list();

    // Count-based: evict oldest non-checkpoints while over the cap.
    if (all.length > MAX_REVISIONS) {
      const deletable = all.filter(isEvictable).reverse(); // oldest first
      let excess = all.length - MAX_REVISIONS;
      while (excess > 0 && deletable.length) {
        await this.delete(deletable.shift().id);
        excess -= 1;
      }
    }

    // Size-based: same policy against the byte cap.
    all = await this.list();
    let totalSize = all.reduce((sum, r) => sum + (r.stateSize || 0), 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      const deletable = all.filter(isEvictable).reverse(); // oldest first
      while (totalSize > MAX_TOTAL_SIZE && deletable.length) {
        const victim = deletable.shift();
        totalSize -= victim.stateSize || 0;
        await this.delete(victim.id);
      }
    }

    // Only checkpoints left and still over a cap: tell the user instead of
    // silently deleting what they explicitly asked to keep.
    const remaining = await this.list();
    const remainingSize = remaining.reduce((sum, r) => sum + (r.stateSize || 0), 0);
    if (
      (remaining.length > MAX_REVISIONS || remainingSize > MAX_TOTAL_SIZE) &&
      !remaining.some(isEvictable)
    ) {
      reportError('Revisions', new Error('checkpoint storage limit reached'), {
        user: 'toast',
        message: 'Checkpoint storage is full — delete checkpoints you no longer need to free space',
        detail: 'prune found only checkpoints over the cap; nothing was deleted',
        id: 'revision-quota'
      });
    }
  }

  /**
   * Restore a revision — replaces current state entirely.
   *
   * THE ORDER IS THE SAFETY PROPERTY (TL-4). This used to be: capture →
   * apply → `history.clear()` → *then* try to write the net, best-effort,
   * `console.warn` on failure — while a confirm dialog promised the net
   * unconditionally. Everything after `_applyState` is past the point of no
   * return: the session history is gone, so a failed write left the
   * pre-restore state unrecoverable and said nothing.
   *
   * It is now: capture (synchronously, as before) → write the net and READ IT
   * BACK → only then apply. A failed safety write THROWS and applies nothing,
   * so the worst case is a refused restore rather than a silent loss.
   *
   * The active project id and load epoch are revalidated immediately before
   * the apply — a project switch mid-flight cancels, never cross-applies.
   */
  async restore(id, opts = {}) {
    const expectedProjectId = this._getProjectId() ?? null;
    const expectedEpoch = this._getEpoch();
    const revision = await this.get(id);
    if (!revision) throw new Error('Revision not found');
    if (
      this._getEpoch() !== expectedEpoch ||
      (this._getProjectId() ?? null) !== expectedProjectId
    ) {
      throw new Error('Project changed — restore cancelled');
    }

    // Capture current state synchronously before replacing it
    const preRestoreState = this._cloneCurrentState();

    // THE NET, BEFORE THE POINT OF NO RETURN. Written and read back; a failure
    // here aborts with NOTHING applied and the session history intact.
    let safety;
    try {
      safety = await this.saveProtected('Before restore', preRestoreState);
    } catch (err) {
      reportError('Revisions', err, {
        user: 'toast',
        message: 'Restore cancelled — a "Before restore" point could not be saved',
        detail: 'safety snapshot write or read-back failed; nothing was changed',
        id: 'revision-safety'
      });
      throw err;
    }

    // Exactly one safety point is exempt from pruning at a time. Housekeeping
    // only: failing to demote the PREVIOUS one leaves two protected records,
    // which costs a little space and loses nothing, so it must never take the
    // restore down with it.
    try {
      await this._releasePreviousProtected(safety.id);
    } catch (err) {
      reportError('Revisions', err, {
        user: 'silent',
        detail: 'could not demote the previous protected safety point'
      });
    }

    // Now replace state. applyLoadedProjectState init()s and syncs the autosave
    // slot so a refresh right after a version restore recovers the restored
    // project, not the pre-restore one.
    const restored = this._migrate(structuredClone(revision.state));
    await this._applyState(restored, { ...opts, label: 'version restore' });
    this._clearHistory();

    // prune() CANNOT FAIL — it toasts and returns — so it is never asked
    // whether the net exists; the read-back above already answered that. It
    // runs here only to keep the store under its caps, and `protected` is what
    // stops it eating the record written six lines ago.
    await this.prune();

    return { restored, safetyPointId: safety.id };
  }

  /** Total storage size across the active project's revisions. */
  async getTotalSize() {
    const all = await this.list();
    return all.reduce((sum, r) => sum + (r.stateSize || 0), 0);
  }
}
